import type { JSX } from "preact";
import { fmtPct } from "./format";

export type Household = "single" | "couple";

export interface PersonFormState {
  dob: string;
  benefitMode: "pia" | "aime";
  pia: string;
  aime: string;
  longevityAge: number;
  claimAge: number;
}

export interface FormState {
  household: Household;
  primary: PersonFormState;
  spouse: PersonFormState;
  colaRate: number;
}

interface FormProps {
  state: FormState;
  onChange: (next: FormState) => void;
  onSubmit: () => void;
  onReset: () => void;
  error: string | null;
}

function PersonFields({
  label,
  person,
  onChange,
  idPrefix,
}: {
  label: string;
  person: PersonFormState;
  onChange: (next: PersonFormState) => void;
  idPrefix: string;
}) {
  const set = <K extends keyof PersonFormState>(key: K, value: PersonFormState[K]) =>
    onChange({ ...person, [key]: value });

  return (
    <fieldset class="tca-ss-calc__fieldset">
      <legend>{label}</legend>

      <div class="tca-ss-calc__row">
        <label class="tca-ss-calc__label" for={`${idPrefix}-dob`}>
          Date of birth
        </label>
        <input
          id={`${idPrefix}-dob`}
          class="tca-ss-calc__input"
          type="date"
          value={person.dob}
          max="2010-12-31"
          min="1900-01-01"
          onInput={(e) => set("dob", (e.target as HTMLInputElement).value)}
          required
        />
      </div>

      <div class="tca-ss-calc__row">
        <span class="tca-ss-calc__label" id={`${idPrefix}-mode-label`}>
          Enter your benefit as
        </span>
        <div
          class="tca-ss-calc__toggle"
          role="radiogroup"
          aria-labelledby={`${idPrefix}-mode-label`}
        >
          <label class="tca-ss-calc__toggle-option">
            <input
              type="radio"
              name={`${idPrefix}-mode`}
              value="pia"
              checked={person.benefitMode === "pia"}
              onChange={() => set("benefitMode", "pia")}
            />
            <span>PIA from my SSA statement</span>
          </label>
          <label class="tca-ss-calc__toggle-option">
            <input
              type="radio"
              name={`${idPrefix}-mode`}
              value="aime"
              checked={person.benefitMode === "aime"}
              onChange={() => set("benefitMode", "aime")}
            />
            <span>Average Indexed Monthly Earnings (AIME)</span>
          </label>
        </div>
      </div>

      {person.benefitMode === "pia" ? (
        <div class="tca-ss-calc__row">
          <label class="tca-ss-calc__label" for={`${idPrefix}-pia`}>
            Primary Insurance Amount (monthly, today's dollars)
          </label>
          <input
            id={`${idPrefix}-pia`}
            class="tca-ss-calc__input"
            type="number"
            inputMode="decimal"
            min="0"
            step="1"
            placeholder="e.g. 2400"
            value={person.pia}
            onInput={(e) => set("pia", (e.target as HTMLInputElement).value)}
          />
          <p class="tca-ss-calc__hint">
            Find this on your latest{" "}
            <a
              href="https://www.ssa.gov/myaccount/"
              target="_blank"
              rel="noopener noreferrer"
            >
              SSA statement
            </a>{" "}
            — the benefit at your full retirement age.
          </p>
        </div>
      ) : (
        <div class="tca-ss-calc__row">
          <label class="tca-ss-calc__label" for={`${idPrefix}-aime`}>
            Average Indexed Monthly Earnings (USD/month)
          </label>
          <input
            id={`${idPrefix}-aime`}
            class="tca-ss-calc__input"
            type="number"
            inputMode="decimal"
            min="0"
            step="1"
            placeholder="e.g. 6500"
            value={person.aime}
            onInput={(e) => set("aime", (e.target as HTMLInputElement).value)}
          />
          <p class="tca-ss-calc__hint">
            We'll apply the SSA bend-point formula (90% / 32% / 15%) to derive
            your PIA.
          </p>
        </div>
      )}

      <div class="tca-ss-calc__row">
        <label class="tca-ss-calc__label" for={`${idPrefix}-longevity`}>
          Longevity assumption: <strong>{person.longevityAge}</strong>
        </label>
        <input
          id={`${idPrefix}-longevity`}
          class="tca-ss-calc__slider"
          type="range"
          min="70"
          max="100"
          step="1"
          value={person.longevityAge}
          onInput={(e) =>
            set("longevityAge", Number((e.target as HTMLInputElement).value))
          }
        />
        <p class="tca-ss-calc__hint">
          The age you want the calculator to project benefits through.
        </p>
      </div>

      <div class="tca-ss-calc__row">
        <label class="tca-ss-calc__label" for={`${idPrefix}-claim`}>
          Custom claim age to compare: <strong>{person.claimAge}</strong>
        </label>
        <input
          id={`${idPrefix}-claim`}
          class="tca-ss-calc__slider"
          type="range"
          min="62"
          max="70"
          step="1"
          value={person.claimAge}
          onInput={(e) =>
            set("claimAge", Number((e.target as HTMLInputElement).value))
          }
        />
        <p class="tca-ss-calc__hint">
          The calculator always shows 62, full retirement age, and 70. Pick any
          additional age between 62 and 70 to compare.
        </p>
      </div>
    </fieldset>
  );
}

export function Form({ state, onChange, onSubmit, onReset, error }: FormProps): JSX.Element {
  const setHousehold = (value: Household) => onChange({ ...state, household: value });
  const setPrimary = (next: PersonFormState) =>
    onChange({ ...state, primary: next });
  const setSpouse = (next: PersonFormState) =>
    onChange({ ...state, spouse: next });
  const setCola = (value: number) => onChange({ ...state, colaRate: value });

  function handleSubmit(e: JSX.TargetedEvent<HTMLFormElement>) {
    e.preventDefault();
    onSubmit();
  }

  return (
    <form class="tca-ss-calc__form" onSubmit={handleSubmit} noValidate>
      <fieldset class="tca-ss-calc__fieldset">
        <legend>Who is this for?</legend>
        <div class="tca-ss-calc__toggle" role="radiogroup" aria-label="Household type">
          <label class="tca-ss-calc__toggle-option">
            <input
              type="radio"
              name="household"
              value="single"
              checked={state.household === "single"}
              onChange={() => setHousehold("single")}
            />
            <span>Just me</span>
          </label>
          <label class="tca-ss-calc__toggle-option">
            <input
              type="radio"
              name="household"
              value="couple"
              checked={state.household === "couple"}
              onChange={() => setHousehold("couple")}
            />
            <span>Me and my spouse</span>
          </label>
        </div>
      </fieldset>

      <PersonFields
        label={state.household === "couple" ? "About you" : "Your details"}
        person={state.primary}
        onChange={setPrimary}
        idPrefix="primary"
      />

      {state.household === "couple" && (
        <PersonFields
          label="About your spouse"
          person={state.spouse}
          onChange={setSpouse}
          idPrefix="spouse"
        />
      )}

      <fieldset class="tca-ss-calc__fieldset">
        <legend>Inflation assumption</legend>
        <div class="tca-ss-calc__row">
          <label class="tca-ss-calc__label" for="cola">
            Assumed annual COLA: <strong>{fmtPct(state.colaRate)}</strong>
          </label>
          <input
            id="cola"
            class="tca-ss-calc__slider"
            type="range"
            min="0"
            max="0.05"
            step="0.001"
            value={state.colaRate}
            onInput={(e) =>
              setCola(Number((e.target as HTMLInputElement).value))
            }
          />
          <p class="tca-ss-calc__hint">
            SSA publishes COLA each October. Recent: 2022 = 5.9%, 2023 = 8.7%,
            2024 = 3.2%, 2025 = 2.5%, 2026 = 2.8%. Default 2.5% is a long-run
            illustrative assumption — not a forecast.
          </p>
        </div>
      </fieldset>

      {error && (
        <div class="tca-ss-calc__error" role="alert">
          {error}
        </div>
      )}

      <div class="tca-ss-calc__actions">
        <button type="submit" class="tca-ss-calc__btn tca-ss-calc__btn--primary">
          Show my scenario
        </button>
        <button
          type="button"
          class="tca-ss-calc__btn tca-ss-calc__btn--ghost"
          onClick={onReset}
        >
          Reset
        </button>
      </div>
    </form>
  );
}
